// Iter-206d — Detail dialog for an upcoming auto-invoice send.
// Surfaces:
//   - Mission/Client/period header
//   - Invoice preview (lines + totals)
//   - Email preview (to, subject, body)
//   - Download PDF aperçu
//   - Actions: Pause / Resume / Cancel (with reason)
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Download, PauseCircle, PlayCircle, XCircle, AlertTriangle, Mail, FileText, CheckCircle2,
} from "lucide-react";

const fmtMoney = (v, ccy) =>
  `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${ccy || ""}`.trim();

export default function UpcomingInvoiceDetail({ open, slot, onClose, onChanged }) {
  // slot = { mission_id, mission_title, year, month, send_mode, reason, override }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // pause|resume|cancel
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const load = async () => {
    if (!slot) return;
    setLoading(true);
    try {
      const r = await api.get(`/billing/auto-invoice/upcoming/${slot.mission_id}/${slot.year}/${slot.month}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger l'aperçu");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && slot) load(); else { setData(null); } /* eslint-disable-next-line */ }, [open, slot?.mission_id, slot?.year, slot?.month]);

  if (!slot) return null;

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/billing/auto-invoice/upcoming/${slot.mission_id}/${slot.year}/${slot.month}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apercu_${slot.year}-${String(slot.month).padStart(2,'0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du téléchargement");
    }
  };

  const doPause = async () => {
    setBusy("pause");
    try {
      await api.post(`/billing/auto-invoice/upcoming/${slot.mission_id}/${slot.year}/${slot.month}/pause`);
      toast.success("Envoi mis en pause");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };

  const doResume = async () => {
    setBusy("resume");
    try {
      const r = await api.post(`/billing/auto-invoice/upcoming/${slot.mission_id}/${slot.year}/${slot.month}/resume`);
      const ir = r.data?.immediate_run;
      if (ir) {
        const st = ir.status || "—";
        if (["DONE", "SHADOW"].includes(st)) {
          toast.success(`Envoi repris et déclenché immédiatement — statut : ${st}`);
        } else {
          toast.warning(`Repris — déclenchement immédiat retourné : ${st}${ir.reason ? " (" + ir.reason + ")" : ""}`);
        }
      } else {
        toast.success("Envoi repris — sera envoyé au prochain tick 04:00 UTC");
      }
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };

  const submitCancel = async () => {
    if (!cancelReason.trim()) { toast.error("Motif requis."); return; }
    setBusy("cancel");
    try {
      await api.post(`/billing/auto-invoice/upcoming/${slot.mission_id}/${slot.year}/${slot.month}/cancel`, { reason: cancelReason });
      toast.success("Envoi annulé");
      setCancelOpen(false);
      setCancelReason("");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };

  const override = data?.override;
  const isPaused = override?.state === "PAUSED";
  const isCancelled = override?.state === "CANCELLED";

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="upcoming-invoice-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              Aperçu de la facture — {slot.mission_title}
              <span className="block text-xs font-mono font-normal text-zinc-500 mt-0.5">
                {data?.client?.name || slot.client_name} · {slot.year}-{String(slot.month).padStart(2, "0")} · {slot.send_mode === "IMMEDIATE" ? "Immédiat" : "Fin de mois"}
              </span>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-10 text-center text-sm text-zinc-500">
              <Loader2 className="inline animate-spin mr-2" size={14} /> Chargement…
            </div>
          ) : !data ? (
            <div className="py-10 text-center text-sm text-zinc-500">Aucun aperçu disponible.</div>
          ) : !data.ready ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>Cette mission n'est pas encore prête à être facturée — {data.missing_cra} CRA non approuvé(s). L'aperçu deviendra disponible une fois tous les CRA validés.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Override banner */}
              {isPaused && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2" data-testid="override-paused-banner">
                  <PauseCircle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Envoi mis en pause</strong> par {override.actor_name || "—"} le {new Date(override.created_at).toLocaleString("fr-FR")}.
                    {override.reason ? <div className="text-xs italic">{override.reason}</div> : null}
                  </div>
                </div>
              )}
              {isCancelled && (
                <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900 flex items-start gap-2" data-testid="override-cancelled-banner">
                  <XCircle size={14} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Envoi annulé</strong> par {override.actor_name || "—"} le {new Date(override.created_at).toLocaleString("fr-FR")}.
                    {override.reason ? <div className="text-xs italic">Motif : {override.reason}</div> : null}
                  </div>
                </div>
              )}

              {/* Invoice preview */}
              <section>
                <h3 className="font-heading font-bold text-sm text-zinc-900 flex items-center gap-2 mb-2">
                  <FileText size={14} /> Détails de la facture
                </h3>
                <div className="border border-zinc-200 rounded-md overflow-hidden bg-white text-sm" data-testid="invoice-preview-block">
                  <div className="grid grid-cols-3 gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-200">
                    <div>N° (estimé) : <strong>{data.invoice_preview.next_number_estimate}</strong></div>
                    <div>Date facture : <strong>{data.invoice_preview.invoice_date}</strong></div>
                    <div>Échéance : <strong>{data.invoice_preview.due_date}</strong></div>
                  </div>
                  <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50/50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                      <tr>
                        <th className="text-left px-3 py-1.5">Désignation</th>
                        <th className="text-right px-3 py-1.5">Qté (JH)</th>
                        <th className="text-right px-3 py-1.5">TJM</th>
                        <th className="text-right px-3 py-1.5">Total HT</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {(data.invoice_preview.lines || []).map((ln, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">{ln.description}</td>
                          <td className="px-3 py-2 text-right font-mono">{Number(ln.quantity_jh || 0).toLocaleString("fr-FR")}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.tjm, data.invoice_preview.currency)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(ln.total_ht, data.invoice_preview.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <div className="px-3 py-2 border-t border-zinc-200 text-right text-xs space-y-0.5 font-mono">
                    <div>Total HT : <strong>{fmtMoney(data.invoice_preview.total_ht, data.invoice_preview.currency)}</strong></div>
                    <div>TVA ({Math.round((data.invoice_preview.vat_rate || 0) * 100)}%) : <strong>{fmtMoney(data.invoice_preview.total_vat, data.invoice_preview.currency)}</strong></div>
                    <div className="text-sm">Total TTC : <strong>{fmtMoney(data.invoice_preview.total_ttc, data.invoice_preview.currency)}</strong></div>
                  </div>
                </div>
                <div className="mt-2">
                  <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-pdf-preview-btn">
                    <Download size={12} className="mr-1.5" /> Télécharger l'aperçu PDF
                  </Button>
                </div>
              </section>

              {/* Email preview */}
              <section>
                <h3 className="font-heading font-bold text-sm text-zinc-900 flex items-center gap-2 mb-2">
                  <Mail size={14} /> Email qui sera envoyé
                </h3>
                <div className="border border-zinc-200 rounded-md bg-white" data-testid="email-preview-block">
                  <div className="px-3 py-2 border-b border-zinc-100 text-xs space-y-0.5">
                    <div><span className="text-zinc-500 font-mono">À :</span> <strong>{data.email_preview.to_name}</strong> &lt;{data.email_preview.to_email}&gt;</div>
                    <div><span className="text-zinc-500 font-mono">Sujet :</span> {data.email_preview.subject}</div>
                  </div>
                  <pre className="px-3 py-3 text-xs whitespace-pre-wrap font-sans text-zinc-800 max-h-64 overflow-y-auto">{data.email_preview.body_text}</pre>
                </div>
              </section>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            {data?.ready && !isCancelled && !isPaused && (
              <Button variant="outline" onClick={doPause} disabled={busy === "pause"} data-testid="upcoming-pause-btn">
                {busy === "pause" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <PauseCircle size={12} className="mr-1.5" />}
                Mettre en pause
              </Button>
            )}
            {data?.ready && isPaused && (
              <Button variant="outline" onClick={doResume} disabled={busy === "resume"} data-testid="upcoming-resume-btn">
                {busy === "resume" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <PlayCircle size={12} className="mr-1.5" />}
                Reprendre
              </Button>
            )}
            {data?.ready && !isCancelled && (
              <Button onClick={() => setCancelOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="upcoming-cancel-btn">
                <XCircle size={12} className="mr-1.5" /> Annuler la facture
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel reason sub-dialog */}
      <Dialog open={cancelOpen} onOpenChange={(o) => { if (!o && busy !== "cancel") setCancelOpen(false); }}>
        <DialogContent data-testid="upcoming-cancel-reason-dialog">
          <DialogHeader>
            <DialogTitle>Motif d'annulation</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-zinc-500">
              Cette action est <strong>définitive</strong> côté UI : l'envoi automatique pour {slot?.year}-{String(slot?.month).padStart(2, "0")} ne sera plus déclenché.
              Pour rétablir, il faudra émettre une facture manuelle.
            </div>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ex. mission interrompue, double comptabilisation, ré-émission manuelle nécessaire…"
              data-testid="cancel-reason-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy === "cancel"}>Retour</Button>
            <Button onClick={submitCancel} disabled={busy === "cancel" || !cancelReason.trim()} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="cancel-reason-confirm-btn">
              {busy === "cancel" ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <CheckCircle2 size={12} className="mr-1.5" />}
              Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
